import { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { Video as VideoIcon, X, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import useModalA11y from "@/lib/useModalA11y";
import { MediaVideo } from "@/lib/mediaSrc";

export default function ClinicianVideos({ patientId }) {
  const [videos, setVideos] = useState([]);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState("");
  const load = () => {
    if (patientId) api.get(`/videos/patient/${patientId}`).then((r) => setVideos(r.data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [patientId]);

  const closePreview = () => setPreview(null);
  const previewRef = useModalA11y({ open: !!preview, onClose: closePreview });

  const reviewComplete = async (v) => {
    if (!window.confirm(`Mark "${v.notes || "this clip"}" as reviewed?\n\nThe video file will be permanently deleted from storage to keep the system lean. The review record stays for the audit trail.`)) return;
    setBusy(v.id);
    try {
      await api.post(`/videos/${v.id}/review-complete`);
      toast.success("Video reviewed and removed from storage.");
      setVideos((cur) => cur.filter((x) => x.id !== v.id));
      if (preview?.id === v.id) setPreview(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not mark as reviewed.");
    } finally { setBusy(""); }
  };

  return (
    <div className="fl-card p-6">
      <div className="fl-heading text-xl mb-4">Patient videos ({videos.length})</div>
      {videos.length === 0 && <div className="text-sm text-zinc-500">No uploads yet.</div>}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {videos.map((v) => (
          <div key={v.id} className="border border-white/10 rounded overflow-hidden hover:border-[#f47a20]/40 transition-colors" data-testid={`cvid-${v.id}`}>
            <button onClick={() => setPreview(v)} className="w-full text-left">
              <div className="aspect-video bg-black flex items-center justify-center"><VideoIcon size={30} className="text-zinc-700" /></div>
              <div className="p-2">
                <div className="text-[10px] text-zinc-500">{new Date(v.created_at).toLocaleDateString("en-GB")}</div>
                <div className="text-xs text-white line-clamp-2">{v.notes || "Untitled"}</div>
              </div>
            </button>
            <button
              onClick={() => reviewComplete(v)}
              disabled={busy === v.id}
              className="w-full text-[11px] px-2 py-1.5 border-t border-white/10 text-emerald-300 hover:text-white hover:bg-emerald-500/10 disabled:opacity-60 flex items-center justify-center gap-1"
              data-testid={`cvid-review-${v.id}`}
            >
              {busy === v.id ? <Loader2 size={11} className="animate-spin"/> : <CheckCircle2 size={11}/>}
              {busy === v.id ? "Removing…" : "Review completed"}
            </button>
          </div>
        ))}
      </div>
      {preview && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-6" onClick={closePreview} data-testid="cvid-preview-modal">
          <div
            ref={previewRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="cvid-preview-title"
            className="max-w-3xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between mb-3 gap-3 flex-wrap">
              <div id="cvid-preview-title" className="text-sm text-white">{preview.notes || "Patient video"}</div>
              <div className="flex gap-2">
                <button
                  onClick={() => reviewComplete(preview)}
                  disabled={busy === preview.id}
                  className="fl-btn-ghost text-xs text-emerald-300 hover:text-white"
                  data-testid={`cvid-preview-review-${preview.id}`}
                >
                  {busy === preview.id ? <Loader2 size={11} className="animate-spin"/> : <CheckCircle2 size={11}/>} Review completed
                </button>
                <button onClick={closePreview} aria-label="Close video preview"><X className="text-zinc-400" /></button>
              </div>
            </div>
            <MediaVideo id={preview.id} controls autoPlay className="w-full rounded border border-white/10" />
          </div>
        </div>
      )}
    </div>
  );
}
